import { useTranslation } from "react-i18next";
import { CalendarClock, UserX, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { OPEN_INSPECTIONS_WINDOW_DAYS } from "@/lib/openInspectionsWindow";

export interface GroupCount {
  groupId: string;
  groupName: string;
  count: number;
}

interface OpenInspectionsCardsProps {
  /** Count of open inspections due within the next 14 days. */
  dueSoonCount: number;
  /** Count of open inspections with no assignee (`assigned_to IS NULL`). */
  unassignedCount: number;
  /** Open inspections grouped by Fachbereich-Gruppe (TES-2017 v2.3: Gruppe statt Team). */
  byGroup: GroupCount[];
  /** Drilldown: focus the table on the next-14-days subset. */
  onDrilldownDueSoon: () => void;
  /** Drilldown: focus the table on unassigned inspections. */
  onDrilldownUnassigned: () => void;
  /** Drilldown: filter the table by a group. */
  onDrilldownGroup: (groupId: string) => void;
}

/**
 * Overview cards for the "Offene Prüfungen" view (TES-506 §1).
 * (a) due in next 14 days · (b) unassigned · (c) grouped by Gruppe (TES-2017).
 * Each card drills down into the table below.
 */
export function OpenInspectionsCards({
  dueSoonCount,
  unassignedCount,
  byGroup,
  onDrilldownDueSoon,
  onDrilldownUnassigned,
  onDrilldownGroup,
}: OpenInspectionsCardsProps) {
  const { t } = useTranslation();

  return (
    <div
      className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3"
      data-testid="open-inspections-cards"
    >
      {/* (a) Due in next N days */}
      <button
        type="button"
        onClick={onDrilldownDueSoon}
        className="text-left"
        data-testid="open-inspections-card-due-soon"
      >
        <Card className="h-full hover:bg-surface-container-low transition-colors cursor-pointer">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-tight">
                  {t("openInspections.cards.dueSoon", { days: OPEN_INSPECTIONS_WINDOW_DAYS })}
                </p>
                <p
                  className="mt-2 text-2xl lg:text-3xl font-bold"
                  data-testid="open-inspections-card-due-soon-value"
                >
                  {dueSoonCount}
                </p>
              </div>
              <div className="p-2 lg:p-3 rounded-lg bg-info/15 shrink-0">
                <CalendarClock className="h-5 w-5 lg:h-6 lg:w-6 text-info" />
              </div>
            </div>
          </CardContent>
        </Card>
      </button>

      {/* (b) Unassigned */}
      <button
        type="button"
        onClick={onDrilldownUnassigned}
        className="text-left"
        data-testid="open-inspections-card-unassigned"
      >
        <Card className="h-full hover:bg-surface-container-low transition-colors cursor-pointer">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-tight">
                  {t("openInspections.cards.unassigned")}
                </p>
                <p
                  className="mt-2 text-2xl lg:text-3xl font-bold"
                  data-testid="open-inspections-card-unassigned-value"
                >
                  {unassignedCount}
                </p>
              </div>
              <div className="p-2 lg:p-3 rounded-lg bg-warning/15 shrink-0">
                <UserX className="h-5 w-5 lg:h-6 lg:w-6 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>
      </button>

      {/* (c) Grouped by Gruppe (TES-2017). data-testids kept stable (…-by-team / …-card-team-<id>)
          so the existing e2e contract holds; the ids now carry a group id. */}
      <Card className="h-full" data-testid="open-inspections-card-by-team">
        <CardContent className="p-4 lg:p-6">
          <div className="flex items-center justify-between gap-2 mb-3">
            <p className="text-xs sm:text-sm text-muted-foreground leading-tight">
              {t("openInspections.cards.byGroup")}
            </p>
            <div className="p-2 rounded-lg bg-primary/15 shrink-0">
              <Users className="h-5 w-5 text-primary" />
            </div>
          </div>
          {byGroup.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("openInspections.cards.byGroupEmpty")}
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-[120px] overflow-auto">
              {byGroup.map((group) => (
                <li key={group.groupId}>
                  <button
                    type="button"
                    onClick={() => onDrilldownGroup(group.groupId)}
                    className="w-full flex items-center justify-between gap-2 rounded px-2 py-1 text-sm hover:bg-muted transition-colors"
                    data-testid={`open-inspections-card-team-${group.groupId}`}
                  >
                    <span className="truncate">{group.groupName}</span>
                    <span className="font-semibold tabular-nums">{group.count}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
